import type { ReactNode } from 'react';

import type { PointerActivationConstraint } from '@dnd-kit/core';
import type { SxProps, Theme } from '@mui/material/styles';

export const DEFAULT_ACTIVATION_CONSTRAINT: PointerActivationConstraint = {
  delay: 12,
  tolerance: 2,
};

export const DEFAULT_OVERLAY_Z_INDEX = 1200;

export type ReorderEvent<T> = {
  items: T[];
  movedItem: T;
  fromIndex: number;
  toIndex: number;
};

export type SortableTableColDef<T> = {
  field: string;
  headerName?: string;
  width?: number | string;
  flex?: number;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  renderCell?: (params: SortableTableCellParams<T>) => ReactNode;
  renderHeader?: () => ReactNode;
  hide?: boolean;
};

export type SortableTableCellParams<T> = {
  row: T;
  value: unknown;
  field: string;
};

export type UploadingItem = {
  id: string;
  name: string;
  type?: string;
  progress?: number;
  error?: Error;
};

export type SortableTableProps<T extends { id: string }> = {
  items: T[];
  columns: SortableTableColDef<T>[];
  onReorder?: (event: ReorderEvent<T>) => void;
  enableDragAndDrop?: boolean;
  uploadingItems?: UploadingItem[];
  /** Render function for uploading items - renders in first visible column, rest are empty */
  renderUploadingItem?: (item: UploadingItem) => ReactNode;
  showHeader?: boolean;
  loading?: boolean;
  skeletonRows?: number;
  onRowClick?: (item: T) => void;
  selectedItemId?: string | null;
  sx?: SxProps<Theme>;
  rowGap?: number;
  rowHover?: boolean;
  dragHandleWidth?: number;
};

export type SortableTableRowProps<T extends { id: string }> = {
  item: T;
  columns: SortableTableColDef<T>[];
  isDragOverlay?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
  rowHover?: boolean;
  dragHandleWidth?: number;
};

export type SortableTableUploadingRowProps = {
  item: UploadingItem;
  columns: SortableTableColDef<UploadingItem>[];
  dragHandleWidth?: number;
};

export type SortableTableHeaderProps<T> = {
  columns: SortableTableColDef<T>[];
  /** Show an empty spacer column to align with drag handle in rows */
  showDragHandleSpacer?: boolean;
  dragHandleWidth?: number;
};

export const getColumnStyle = <T>(column: SortableTableColDef<T>) => {
  const alignmentStyles: Record<
    string,
    { display: string; justifyContent: string }
  > = {
    center: { display: 'flex', justifyContent: 'center' },
    right: { display: 'flex', justifyContent: 'flex-end' },
  };

  return {
    ...(column.width && { width: column.width, flexShrink: 0 }),
    ...(column.flex && { flex: column.flex, minWidth: column.minWidth ?? 0 }),
    ...(column.align && {
      textAlign: column.align,
      ...alignmentStyles[column.align],
    }),
  };
};
